// @section 1 AccountNextStepLens — the single most-important next action
//
// Reads the BD entities + open tasks for the active account and runs a
// transparent priority engine to surface ONE headline action with rationale,
// plus the next two runners-up. The point is to compress "what should I do
// next on this account?" into a one-glance answer — no per-account analysis
// paralysis, no scrolling between tabs to figure it out.
//
// The priority order (first hit wins for the headline; remaining hits become
// runners-up):
//
//   1. Overdue P1 task                                    → Finish: <task>
//   2. Draft offer with line items, not sent              → Send: v<N> · $<total>
//   3. High-priority open ask not addressed in any offer  → Address: <ask>
//   4. Critical/high finding still 'to-research'          → Research: <finding>
//   5. Fact claimed in meeting:* but not yet re-sourced   → Verify: <fact>
//   6. No deals on the account                            → Create a deal
//   7. Has deals + no asks/findings                       → Capture asks
//
// Each candidate links to the relevant lens (Workplan, Delivery, Asks,
// Findings, Facts, Overview) — the next action is one click away.

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  listBdFacts, listBdAsks, listBdFindings, listBdOffers, listBdOfferItems,
  type AccountAggregate,
  type BdFact, type BdAsk, type BdFinding, type BdOffer, type BdOfferItem,
} from '../../api';

type Candidate = {
  rule: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  rank: number;                       // sort order within rule (lower = higher)
  title: string;                      // "Send: v2 draft offer · $12,500"
  rationale: string;                  // "Draft offer has 4 line items and isn't sent yet."
  lensKey: 'pm' | 'delivery' | 'asks' | 'findings' | 'facts' | 'overview';
  tone: string;                       // color accent
};

const RULE_LABELS: Record<Candidate['rule'], string> = {
  1: 'Overdue task',
  2: 'Draft offer to send',
  3: 'Open ask to address',
  4: 'Open research item',
  5: 'Fact to verify',
  6: 'No deals yet',
  7: 'No asks captured',
};

export default function AccountNextStepLens({ data }: { data: AccountAggregate }) {
  const { id: accountId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = data.company.id;

  const [facts, setFacts]     = useState<BdFact[] | null>(null);
  const [asks, setAsks]       = useState<BdAsk[] | null>(null);
  const [findings, setFinds]  = useState<BdFinding[] | null>(null);
  const [offers, setOffers]   = useState<BdOffer[] | null>(null);
  const [offerItems, setOfferItems] = useState<Record<string, BdOfferItem[]>>({});
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listBdFacts(companyId),
      listBdAsks(companyId),
      listBdFindings(companyId),
      listBdOffers({ companyId }),
    ])
      .then(async ([f, a, fi, o]) => {
        if (cancelled) return;
        setFacts(f); setAsks(a); setFinds(fi); setOffers(o);
        const drafts = o.filter((of) => of.status === 'draft');
        const entries = await Promise.all(
          drafts.map((d) => listBdOfferItems(d.id).then((items) => [d.id, items] as const)),
        );
        if (!cancelled) setOfferItems(Object.fromEntries(entries));
      })
      .catch((e) => !cancelled && setErr(String(e)));
    return () => { cancelled = true; };
  }, [companyId]);

  const candidates = useMemo<Candidate[]>(() => {
    if (facts === null || asks === null || findings === null || offers === null) return [];
    return scoreCandidates(data, facts, asks, findings, offers, offerItems);
  }, [data, facts, asks, findings, offers, offerItems]);

  if (err) {
    return <div style={{ color: '#e87e7e' }}>{err}</div>;
  }
  if (facts === null || asks === null || findings === null || offers === null) {
    return <div style={{ color: '#6b7a90', fontSize: 13 }}>Computing next step…</div>;
  }

  const headline = candidates[0];
  const runnersUp = candidates.slice(1, 3);

  function go(lensKey: Candidate['lensKey']) {
    if (!accountId) return;
    navigate(`/accounts/${accountId}/${lensKey}`);
  }

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* @block 1.1 Headline action — the single thing the operator should do */}
      {headline ? (
        <HeadlineCard cand={headline} go={() => go(headline.lensKey)} />
      ) : (
        <EmptyState>
          Nothing urgent. The account has no overdue tasks, no draft offers waiting to ship,
          no high-priority asks, no open research, and no facts to verify. Find the next angle
          in the Overview lens.
        </EmptyState>
      )}

      {/* @block 1.2 Runners-up — the 2 next-most-important things, in case the
          headline isn't the one you want to act on right now. */}
      {runnersUp.length > 0 && (
        <div>
          <div style={sectionLabel}>Then</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {runnersUp.map((c, i) => (
              <RunnerUpCard key={`${c.rule}-${i}`} cand={c} go={() => go(c.lensKey)} />
            ))}
          </div>
        </div>
      )}

      {/* @block 1.3 Rules legend — transparent so the operator can predict ordering */}
      <Legend />
    </div>
  );
}

// @section 2 Scoring engine
//
// Iterates each rule in order. For rules 1–5, multiple hits each become a
// candidate (so they can appear as runners-up). For 6–7, only one candidate
// is emitted since they're "state of the account" signals.
//
// The output is sorted by (rule asc, rank asc), so rule 1 hits beat rule 2
// hits, and within rule 2, smaller rank values (e.g. larger offer total)
// come first.
function scoreCandidates(
  data: AccountAggregate,
  facts: BdFact[],
  asks: BdAsk[],
  findings: BdFinding[],
  offers: BdOffer[],
  offerItems: Record<string, BdOfferItem[]>,
): Candidate[] {
  const cands: Candidate[] = [];
  const today = new Date().toISOString().slice(0, 10);

  // Rule 1 — overdue P1 tasks
  for (const t of data.openTasks ?? []) {
    if (t.priority === 1 && t.dueDate && t.dueDate < today) {
      cands.push({
        rule: 1, rank: t.dueDate.length > 0 ? new Date(t.dueDate).getTime() : 0,
        title: `Finish: ${t.title}`,
        rationale: `P1 task overdue (due ${t.dueDate}).`,
        lensKey: 'pm', tone: '#e87e7e',
      });
    }
  }

  // Rule 2 — draft offer with line items, not sent
  const drafts = offers.filter((o) => o.status === 'draft');
  for (const o of drafts) {
    const items = offerItems[o.id] ?? [];
    if (items.length > 0) {
      const total = o.totalCents ?? items.reduce((s, it) => s + it.lineTotalCents, 0);
      const dealName = data.deals.find((d) => d.id === o.dealId)?.name ?? 'a deal';
      cands.push({
        rule: 2, rank: -total,                              // bigger total first
        title: `Send draft v${o.version}${total > 0 ? ` · ${fmtMoney(total, o.currency)}` : ''}`,
        rationale: `Draft offer on ${dealName} has ${items.length} line item${items.length === 1 ? '' : 's'} and hasn't shipped yet.`,
        lensKey: 'delivery', tone: '#a08fd8',
      });
    }
  }

  // Rule 3 — high-priority open asks NOT addressed in any offer line item
  const addressedAskIds = new Set<string>();
  for (const list of Object.values(offerItems)) {
    for (const it of list) if (it.addressesAskId) addressedAskIds.add(it.addressesAskId);
  }
  for (const a of asks) {
    if (a.status === 'open' && a.priority === 'high' && !addressedAskIds.has(a.id)) {
      cands.push({
        rule: 3, rank: 0,
        title: `Address: ${a.ask}`,
        rationale: `High-priority open ask with no offer line item yet.`,
        lensKey: 'asks', tone: '#7eb6e8',
      });
    }
  }
  // Medium-priority open asks fall back here too (lower priority than high)
  for (const a of asks) {
    if (a.status === 'open' && a.priority === 'medium' && !addressedAskIds.has(a.id)) {
      cands.push({
        rule: 3, rank: 10,
        title: `Address: ${a.ask}`,
        rationale: `Medium-priority open ask with no offer line item yet.`,
        lensKey: 'asks', tone: '#7eb6e8',
      });
    }
  }

  // Rule 4 — critical/high findings still to-research
  for (const f of findings) {
    if (f.status !== 'to-research') continue;
    if (f.severity === 'critical' || f.severity === 'high') {
      cands.push({
        rule: 4, rank: f.severity === 'critical' ? 0 : 10,
        title: `Research: ${f.title}`,
        rationale: `${f.severity === 'critical' ? 'Critical' : 'High-severity'} ${f.category} finding still to-research.`,
        lensKey: 'findings', tone: '#f0a96b',
      });
    }
  }

  // Rule 5 — facts claimed in meeting:* but not yet re-sourced
  for (const f of facts) {
    if (f.source && f.source.toLowerCase().startsWith('meeting:')) {
      cands.push({
        rule: 5, rank: 0,
        title: `Verify: ${labelize(f.key)} = ${f.value}`,
        rationale: `Claimed in ${f.source} but not yet confirmed in a client system.`,
        lensKey: 'facts', tone: '#f0c674',
      });
    }
  }

  // Rule 6 — no deals on the account at all
  if (data.deals.length === 0) {
    cands.push({
      rule: 6, rank: 0,
      title: `Create a deal for ${data.company.name}`,
      rationale: `Without a deal, BD facts/asks/findings have nothing to attach to.`,
      lensKey: 'overview', tone: '#6ec78a',
    });
  }

  // Rule 7 — has deals but no asks AND no findings
  if (data.deals.length > 0 && asks.length === 0 && findings.length === 0) {
    cands.push({
      rule: 7, rank: 0,
      title: `Capture asks from the next conversation`,
      rationale: `Account has deals but no asks or findings yet — the BD loop hasn't started.`,
      lensKey: 'asks', tone: '#7eb6e8',
    });
  }

  cands.sort((a, b) => (a.rule - b.rule) || (a.rank - b.rank));
  return cands;
}

function labelize(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

function fmtMoney(cents?: number | null, currency: string = 'USD'): string {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toLocaleString()}`;
  }
}

// @section 3 Headline + runner-up cards
function HeadlineCard({ cand, go }: { cand: Candidate; go: () => void }) {
  return (
    <section style={{
      padding: '20px 24px',
      border: `1px solid ${cand.tone}44`,
      borderLeft: `4px solid ${cand.tone}`,
      borderRadius: 8,
      background: '#171c26',
      display: 'grid',
      gap: 10,
    }}>
      <div style={{
        fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700,
        color: cand.tone,
      }}>
        Next step · {RULE_LABELS[cand.rule]}
      </div>
      <div style={{ fontSize: 22, color: '#e6e8ee', fontWeight: 500, lineHeight: 1.3 }}>
        {cand.title}
      </div>
      <div style={{ fontSize: 13, color: '#a3b1c2', lineHeight: 1.5 }}>
        {cand.rationale}
      </div>
      <div>
        <button
          onClick={go}
          style={{
            marginTop: 4,
            padding: '8px 16px',
            border: `1px solid ${cand.tone}88`,
            borderRadius: 5,
            background: `${cand.tone}22`,
            color: '#e6e8ee',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Open {lensLabel(cand.lensKey)} →
        </button>
      </div>
    </section>
  );
}

function RunnerUpCard({ cand, go }: { cand: Candidate; go: () => void }) {
  return (
    <div
      onClick={go}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
      style={{
        padding: '10px 14px',
        border: '1px solid #232a37',
        borderLeft: `3px solid ${cand.tone}88`,
        borderRadius: 6,
        background: '#1d2330',
        cursor: 'pointer',
        display: 'grid',
        gap: 4,
      }}
    >
      <div style={{
        fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.5,
        color: cand.tone, fontWeight: 600,
      }}>
        {RULE_LABELS[cand.rule]}
      </div>
      <div style={{ fontSize: 14, color: '#e6e8ee', fontWeight: 500 }}>{cand.title}</div>
      <div style={{ fontSize: 11, color: '#6b7a90' }}>{cand.rationale}</div>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <section style={{
      padding: '24px 28px',
      border: '1px solid #232a37',
      borderRadius: 8,
      background: '#171c26',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#6ec78a',
        marginBottom: 8, fontWeight: 700,
      }}>
        Caught up
      </div>
      <div style={{ fontSize: 14, color: '#a3b1c2', lineHeight: 1.6, maxWidth: 520, margin: '0 auto' }}>
        {children}
      </div>
    </section>
  );
}

function Legend() {
  const rows: { num: number; rule: string }[] = [
    { num: 1, rule: 'Overdue P1 tasks' },
    { num: 2, rule: 'Draft offers with line items, not sent' },
    { num: 3, rule: 'High / medium-priority asks not yet in any offer' },
    { num: 4, rule: 'Critical / high findings still to-research' },
    { num: 5, rule: 'Facts claimed in a meeting but not yet verified' },
    { num: 6, rule: 'No deals on the account' },
    { num: 7, rule: 'Has deals but no asks / findings yet' },
  ];
  return (
    <details style={{
      padding: 10, border: '1px dashed #2a3346', borderRadius: 6,
      background: 'transparent', fontSize: 11, color: '#6b7a90', lineHeight: 1.6,
    }}>
      <summary style={{ cursor: 'pointer', color: '#a3b1c2', fontWeight: 600 }}>
        How "next step" is chosen
      </summary>
      <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        {rows.map((r) => (
          <li key={r.num}><strong style={{ color: '#a3b1c2' }}>{r.rule}</strong></li>
        ))}
      </ol>
      <div style={{ marginTop: 8 }}>
        First rule that fires wins the headline; remaining hits appear as runners-up.
      </div>
    </details>
  );
}

function lensLabel(key: Candidate['lensKey']): string {
  switch (key) {
    case 'pm':       return 'Workplan';
    case 'delivery': return 'Delivery';
    case 'asks':     return 'Asks';
    case 'findings': return 'Findings';
    case 'facts':    return 'Facts';
    case 'overview': return 'Overview';
  }
}

const sectionLabel: CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: '#6b7a90',
  fontWeight: 600,
  marginBottom: 8,
};
